import { createFileRoute } from "@tanstack/react-router";
import { Employee } from "../../api/generated";
import { useState } from "react";
import { EmployeeRegisterStepOne } from "../../components/employee_register_step_one";
import { EmployeeRegisterStepTwo } from "../../components/employee_register_step_two";
import { EmployeeRegisterStepThree } from "../../components/employee_register_step_three";
import { MaterialIssueForm } from "../../components/form/materialIssueForm";

export const Route = createFileRoute("/employee/register/")({
  component: RouteComponent,
});

type StepNumber = 1 | 2 | 3 | 4;

const steps = [
  {
    name: "Zoeken",
    number: 1 as StepNumber,
  },
  {
    name: "Registeren",
    number: 2 as StepNumber,
  },
  {
    name: "Bevestigen",
    number: 3 as StepNumber,
  },
  {
    name: "Materiaal uitgeven",
    number: 4 as StepNumber,
  },
];

type RegisterState =
  | { number: 1 }
  | { number: 2; employee: Employee }
  | { number: 3; employee: Employee }
  | { number: 4; employee: Employee };

function canNavigateBack(current: StepNumber, target: StepNumber): boolean {
  return target < current;
}

function buildStateForStep(
  target: StepNumber,
  current: RegisterState,
): RegisterState {
  if (target === 1) {
    return { number: 1 };
  }

  if (!("employee" in current)) {
    return { number: 1 };
  }

  if (target === 2) {
    return { number: 2, employee: current.employee };
  }

  if (target === 3) {
    return { number: 3, employee: current.employee };
  }

  return { number: 4, employee: current.employee };
}

function RouteComponent() {
  const [registerState, setRegisterState] = useState<RegisterState>({
    number: 1,
  });

  const goToStep = (target: StepNumber) => {
    if (!canNavigateBack(registerState.number, target)) {
      return;
    }

    setRegisterState(buildStateForStep(target, registerState));
  };

  return (
    <div className="flex justify-center items-center mt-10">
      <div className="flex w-3/4 flex-col items-center">
        <h1 className="text-2xl font-bold mb-4">Medewerker aanmelden</h1>
        <ul className="steps">
          {steps.map((step) => (
            <li
              key={step.number}
              role="button"
              tabIndex={
                canNavigateBack(registerState.number, step.number) ? 0 : -1
              }
              onClick={() => goToStep(step.number)}
              className={`step ${step.number <= registerState.number ? "step-primary" : ""} ${
                canNavigateBack(registerState.number, step.number)
                  ? "cursor-pointer"
                  : "cursor-default"
              }`}
            >
              <span>{step.name}</span>
            </li>
          ))}
        </ul>

        {registerState.number === 1 && (
          <EmployeeRegisterStepOne
            onSelectEmployee={(employee) =>
              setRegisterState({ number: 2, employee })
            }
          />
        )}

        {registerState.number === 2 && (
          <EmployeeRegisterStepTwo
            employee={registerState.employee}
            onConfirm={(employee) => setRegisterState({ number: 3, employee })}
          />
        )}

        {registerState.number === 3 && (
          <EmployeeRegisterStepThree
            employee={registerState.employee}
            onSaveAndContinue={() => setRegisterState({ number: 1 })}
            onIssueMaterial={() =>
              setRegisterState({ number: 4, employee: registerState.employee })
            }
          />
        )}

        {registerState.number === 4 && (
          <MaterialIssueForm
            employee={registerState.employee}
            mode="issue"
            onSave={() => setRegisterState({ number: 1 })}
          />
        )}
      </div>
    </div>
  );
}
